/** Who else is here, what they're looking at, and (in live sessions) their cursors. */
import { useSyncExternalStore } from "react";
import type { Id } from "./types";

export interface Peer {
  connId: string;
  userId: Id;
  name: string;
  location: string | null;
  entityId: Id | null;
  typing: boolean;
  cursor: { x: number; y: number; scope: string } | null;
  drag: unknown;
  at: string;
}

export interface PresenceState {
  location?: string | null;
  entityId?: Id | null;
  typing?: boolean;
  cursor?: { x: number; y: number; scope: string } | null;
  drag?: unknown;
}

type Send = (msg: unknown) => void;

class PresenceStore {
  peers = new Map<string, Peer>();
  myConnId: string | null = null;
  private snapshot: Peer[] = [];
  private listeners = new Set<() => void>();
  private mine: PresenceState = {};
  private send: Send | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private lastSent = 0;

  attach(send: Send | null) {
    this.send = send;
    if (send) this.flush();
  }

  handle(msg: { type: string; peer?: Peer; peers?: Peer[]; connId?: string }) {
    if (msg.type === "presence.snapshot") {
      this.peers.clear();
      this.myConnId = msg.connId ?? null;
      for (const p of msg.peers ?? []) this.peers.set(p.connId, p);
    } else if (msg.type === "presence" && msg.peer) {
      this.peers.set(msg.peer.connId, msg.peer);
    } else if (msg.type === "presence.leave" && msg.connId) {
      this.peers.delete(msg.connId);
    }
    this.snapshot = [...this.peers.values()];
    this.listeners.forEach((l) => l());
  }

  clear() {
    this.peers.clear();
    this.snapshot = [];
    this.listeners.forEach((l) => l());
  }

  /** Merges into what we tell others; sends at most ~20 times a second. */
  set(patch: PresenceState) {
    this.mine = { ...this.mine, ...patch };
    const wait = 50 - (Date.now() - this.lastSent);
    if (wait <= 0) this.flush();
    else if (!this.timer) this.timer = setTimeout(() => this.flush(), wait);
  }

  private flush() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (!this.send) return;
    this.lastSent = Date.now();
    this.send({ type: "presence", ...this.mine });
  }

  subscribe = (cb: () => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };
  getSnapshot = () => this.snapshot;
}

export const presence = new PresenceStore();

export function usePeers(): Peer[] {
  return useSyncExternalStore(presence.subscribe, presence.getSnapshot);
}
